import { useMemo, useState } from 'react';
import {
  Card,
  CreateNewCard,
  CreateNewCardHolder,
  Error,
  LoadingStatus,
  Modal,
  NoFound,
} from '@/components/generic';
import { TIER_DATA } from '@/constants/app';
import {
  useGetCardTypes,
  useGetCurrentBusinessInfo,
  useGetTiers,
} from '@xhype/card-app-sdk';
import {
  useGetCardHolderData,
  useGetCards,
  useGetMultiInfoCard,
  useGetOnchainUserTxs,
} from '@package/smart-wallet/hooks';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import CardType from './CardType';
import Tier from './Tier';

const Pricing = () => {
  const { t } = useTranslation();
  const [selectedTier, setSelectedTier] = useState(null);
  const [isModalCreateCardOpen, setIsModalCreateCardOpen] = useState(false);
  const [isModalOpenCreateCardHolder, setIsModalOpenCreateCardHolder] =
    useState(false);
  const [selectedCardType, setSelectedCardType] = useState<any>(null);
  const {
    isLoading: isLoadingGetcards,
    cardsData,
    error: errorGetcards,
  } = useGetCards();
  /////////////////////////////////////
  const { isLoading: isLoadingbusinessInfo, data: businessInfo } =
    useGetCurrentBusinessInfo({
      enabled: true,
    });
  const businessId = useMemo(() => {
    if (!businessInfo) return 0;
    return businessInfo?.id;
  }, [businessInfo]);
  const { isLoading: isLoading_Tiers, data: tiersData } = useGetTiers({
    businessId: Number(businessId),
    enabled: !!businessId,
  });
  /////////////////////////////////////
  const availableTiers = useMemo(() => {
    if (!tiersData || tiersData.length === 0) return [];
    const tierDataKeys = Object.keys(TIER_DATA);
    return tiersData
      .filter((tier: any) => tierDataKeys.includes(tier.name))
      .map((tier: any) => ({
        ...tier,
        info: TIER_DATA[tier.name as keyof typeof TIER_DATA],
        // Keep the API id as the primary id
        id: tier.id,
      }))
      .sort(
        (a: any, b: any) =>
          (a.info.annualPrice || 0) - (b.info.annualPrice || 0),
      );
  }, [tiersData]);
  /////////////////////////////////////
  const { isLoading: isLoadingCardHolder, cardHolderInfo } =
    useGetCardHolderData();
  /////////////////////////////////////
  const {
    records: transactionsData,
    isLoading: isLoadingTransactions,
    error: transactionError,
  } = useGetOnchainUserTxs({
    pageNum: 1,
  });
  /////////////////////////////////////
  /////////////////////////////////////
  const { isLoading: isLoadingcardTypes, data: cardTypes } = useGetCardTypes({
    enabled: true,
  });
  const cardIds = useMemo(() => {
    if (cardsData) {
      const ids = cardsData.map((card: any) => Number(card?.id));
      if (ids.length > 0) {
        return ids;
      }
    }
    return [0];
  }, [cardsData]);
  const {
    isLoading: isLoadingGetMultiInfoCard,
    error: errorGetMultiInfoCard,
    cardsData: MulticardsData,
  } = useGetMultiInfoCard({ cardIds });
  const cardTypesToSelect = useMemo(() => {
    if (!cardTypes || cardTypes.length === 0 || !tiersData) return [];
    const tierRules = tiersData?.find(
      (tier: any) => tier?.id === selectedTier,
    )?.rules;
    const value =
      tierRules?.find((rule: any) => rule?.type === 'CARD_TYPE')?.value || '';
    let cardTypesArray: number[] = [];
    if (value || value !== '') {
      cardTypesArray = value.split(',').map((v: string) => Number(v.trim()));
    }
    return (
      cardTypes
        ?.filter((type) => cardTypesArray?.includes(type?.cardTypeId))
        // ?.filter((type) => type?.cardTypeId === 111040)
        .map((cardtype) => {
          const hasCardHolder =
            cardHolderInfo?.records?.find(
              (record: any) => record?.cardTypeId === cardtype?.cardTypeId,
            )?.holderId || false;
          let activeCardCount = 0;
          if (hasCardHolder) {
            activeCardCount =
              MulticardsData?.reduce((acc: number, card: any) => {
                if (
                  //checks it they are on the same card type
                  card?.cardTypeId === cardtype?.cardTypeId &&
                  //checks if card is atleast not cancelled
                  card?.status !== 'cancel'
                ) {
                  return acc + 1;
                }
                return acc;
              }, 0) || 0;
          }
          return {
            ...cardtype,
            hasCardHolder,
            activeCardCount,
          };
        })
    );
  }, [cardTypes, cardHolderInfo, MulticardsData, tiersData, selectedTier]);
  const hasPendingTransactions = useMemo(() => {
    if (!transactionsData || transactionsData.length === 0) return false;
    const pendingStatuses = ['PENDING', 'PROCESSING', 'ONHOLD'];
    return transactionsData.some(
      (transaction: any) =>
        pendingStatuses.includes(transaction.status) &&
        transaction.type === 'CREATE',
    );
  }, [transactionsData]);
  /////////////////////////////////////
  const globalisLoading = useMemo(() => {
    return (
      isLoadingcardTypes ||
      isLoadingGetcards ||
      isLoadingGetMultiInfoCard ||
      isLoadingCardHolder ||
      isLoadingTransactions ||
      isLoadingbusinessInfo ||
      isLoading_Tiers
    );
  }, [
    isLoadingcardTypes,
    isLoadingGetcards,
    isLoadingGetMultiInfoCard,
    isLoadingCardHolder,
    isLoadingTransactions,
    isLoadingbusinessInfo,
    isLoading_Tiers,
  ]);
  const handlerCardtypeSelect = (cardType: any) => {
    const cardtypesArr = availableTiers?.find(
      (tier: any) => tier?.id === selectedTier,
    )
    setSelectedCardType({
      ...cardType,
      tierSelectedInfo: cardtypesArr,
    });
    if (hasPendingTransactions) {
      return toast.error(t('newcard.pending_card_processing'));
    }
    if (cardType?.hasCardHolder) {
      return setIsModalCreateCardOpen(true);
    } else {
      return setIsModalOpenCreateCardHolder(true);
    }
  };
  const openCardTypeSelection = (tier: any) => {
    let cardTypesOptions = []
    if (!cardTypes || cardTypes.length === 0 || !tiersData) {
      cardTypesOptions = [];
    }
    const tierRules = tiersData?.find(
      (tierData: any) => tierData?.id === tier,
    )?.rules;
    const value =
      tierRules?.find((rule: any) => rule?.type === 'CARD_TYPE')?.value || '';
    let cardTypesArray: number[] = [];
    if (value || value !== '') {
      cardTypesArray = value.split(',').map((v: string) => Number(v.trim()));
    }
    cardTypesOptions =
      cardTypes
        ?.filter((type) => cardTypesArray?.includes(type?.cardTypeId))
        .map((cardtype) => {
          const hasCardHolder =
            cardHolderInfo?.records?.find(
              (record: any) => record?.cardTypeId === cardtype?.cardTypeId,
            )?.holderId || false;
          let activeCardCount = 0;
          if (hasCardHolder) {
            activeCardCount =
              MulticardsData?.reduce((acc: number, card: any) => {
                if (
                  //checks it they are on the same card type
                  card?.cardTypeId === cardtype?.cardTypeId &&
                  //checks if card is atleast not cancelled
                  card?.status !== 'cancel'
                ) {
                  return acc + 1;
                }
                return acc;
              }, 0) || 0;
          }
          return {
            ...cardtype,
            hasCardHolder,
            activeCardCount,
          };
        }) || []
    //////////////////////////////////////
    const cardtypesArr = availableTiers?.find(
      (tierData: any) => tierData?.id === tier,
    )
    const cardType = cardTypesOptions[0];
    setSelectedCardType({
      ...cardType,
      tierSelectedInfo: cardtypesArr,
    });
    if (hasPendingTransactions) {
      return toast.error(
        'Seems like a card is already been processed. Please wait until it is completed or cancelled before creating a new one.',
      );
    }
    if (cardType?.hasCardHolder) {
      return setIsModalCreateCardOpen(true);
    } else {
      return setIsModalOpenCreateCardHolder(true);
    }
  };
  // const scrollToCardTypes = async () => {
  //   const cardTypesElement = document.getElementById('card-types-options');
  //   if (cardTypesElement) {
  //     cardTypesElement.scrollIntoView({
  //       behavior: 'smooth',
  //       block: 'start',
  //     });
  //   }
  // };
  return (
    <>
      <div className='card px-12 pt-22  max-lg:p-8 max-lg:px-6'>
        <div className='mx-auto'>
          <div className='mb-22 max-md:mb-14'>
            <div className='mb-10 text-center max-lg:mb-12 max-md:mb-10 max-md:text-left'>
              <div className='max-w-160 mx-auto mb-4 text-h3 max-lg:max-w-120 max-lg:text-h4'>
                {t('newcard.choose_plan')}
              </div>

              <Error
                error={errorGetMultiInfoCard}
                message={
                  errorGetMultiInfoCard?.message ||
                  t('newcard.error_fetching_multi_info')
                }
              />
              <Error
                error={errorGetcards}
                message={
                  errorGetcards?.message ||
                  t('newcard.error_fetching_cards')
                }
              />
              <Error
                error={transactionError}
                message={
                  transactionError?.message ||
                  t('newcard.error_fetching_transactions')
                }
              />
            </div>
            {globalisLoading ? (
              <LoadingStatus />
            ) : (
              <div className='flex overflow-x-auto max-md:scrollbar-thin max-md:scrollbar-thumb-s-stroke2 max-md:scrollbar-track-transparent max-lg:scrollbar-none max-md:-mx-6 max-xl:before:shrink-0 max-xl:before:w-2 max-xl:after:shrink-0 max-xl:after:w-6'>
                {availableTiers?.map((tierData: any) => (
                  <Tier
                    key={tierData?.id}
                    tierData={tierData}
                    setSelectedTier={setSelectedTier}
                    // scrollToCardTypes={scrollToCardTypes}
                    btnHandler={openCardTypeSelection}
                  />
                ))}

                {!!availableTiers && availableTiers?.length === 0 && (
                  <div className='w-full flex justify-center items-center'>
                    <NoFound
                      title={t('newcard.no_tiers_found')}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!globalisLoading && selectedTier !== null && (
        <>
          <Card title={t('newcard.card_types_available')}>
            <div
              id='card-types-options'
              className='relative -mx-3 mb-6 before:absolute before:left-0 before:top-0 before:bottom-0 before:z-3 before:w-6 before:bg-linear-to-r before:from-b-surface2 before:to-transparent before:pointer-events-none after:absolute after:right-0 after:top-0 after:bottom-0 after:z-3 after:w-6 after:bg-linear-to-l after:from-b-surface2 after:to-transparent after:pointer-events-none'
            >
              <div className='flex before:shrink-0 before:w-5 after:shrink-0 after:w-5 overflow-x-auto scroll-smooth scrollbar-none'>
                {!!cardTypesToSelect && cardTypesToSelect.length > 0 ? (
                  cardTypesToSelect?.map((type) => {
                    return (
                      <CardType
                        key={type?.cardTypeId}
                        value={type}
                        hasPendingTransactions={hasPendingTransactions}
                        handlerCardtypeSelect={handlerCardtypeSelect}
                      />
                    );
                  })
                ) : (
                  <Error error message={t('newcard.no_card_types_found')} />
                )}
              </div>
            </div>
          </Card>
        </>
      )}
      <Modal
        open={isModalCreateCardOpen}
        onClose={() => setIsModalCreateCardOpen(false)}
      >
        <CreateNewCard
          onClose={() => setIsModalCreateCardOpen(false)}
          cardTypeData={selectedCardType}
        />
      </Modal>
      <Modal
        open={isModalOpenCreateCardHolder}
        onClose={() => setIsModalOpenCreateCardHolder(false)}
      >
        <CreateNewCardHolder
          onClose={() => setIsModalOpenCreateCardHolder(false)}
          cardTypeData={selectedCardType}
        />
      </Modal>
    </>
  );
};

export default Pricing;
